import type { BadgeColor } from '@civilgrid/ui/components';
import { exportFileStem } from '@civilgrid/utils/src/extract/types';
import { toKML } from '@placemarkio/tokml';
import { pointOnFeature } from '@turf/turf';
import type { MultiLineString, MultiPolygon } from 'geojson';
import JSZip from 'jszip';
import { Notify } from '~/components2/Notify';
import type { TFormValues } from '~/pages/projectDetails/details/common';
import { createFeature } from './geoUtils';
import { notifyExportError } from './notifyExportError';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from './posthogEvents';
import { PlanStatus, type Project } from './types';

export function getRequiredFieldsForBillingTier(tier: Project['billingTier']): Array<keyof TFormValues> {
  if (tier === 'draft') {
    return ['name', 'billingTier'];
  } else if (tier === 'proposal' || tier === 'project' || tier === 'projectPlus') {
    return ['name', 'billingCode', 'projectManagerId'];
  }
  return [];
}

// TODO: Improve this. The default turf behavior is not the greatest but a decent start.
// lng, lat
export function getProjectCenter(geom: MultiPolygon | MultiLineString): [number, number] {
  const point = pointOnFeature(geom);
  return [point.geometry.coordinates[0], point.geometry.coordinates[1]];
}

export const formatProjectAddress = (
  address?: {
    street?: string;
    crossStreets?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  },
  compact?: boolean,
): string => {
  if (!address) {
    return '';
  }

  const streetPart = address.street || address.crossStreets;

  // Create an array of valid address parts
  const addressParts = [];
  if (streetPart && streetPart.length > 0) addressParts.push(streetPart);
  if (address.city && address.city.length > 0) addressParts.push(address.city);
  if (address.state && address.state.length > 0) addressParts.push(address.state);
  if (address.zipCode && address.zipCode.length > 0) addressParts.push(address.zipCode);

  if (compact) {
    const cityDisplay = address.city ?? '';
    const stateDisplay = address.state ?? '';

    if (cityDisplay.length > 0 && stateDisplay.length > 0) {
      return `${cityDisplay}, ${stateDisplay}`;
    } else {
      return '';
    }
  }

  return addressParts.length > 0 ? addressParts.join(', ') : '';
};

export const planStatusLabels: { [key in PlanStatus]: string } = {
  [PlanStatus.Pending]: 'In Review',
  [PlanStatus.NeedsAttention]: 'Needs Review',
  [PlanStatus.Scheduled]: 'Cleared',
};

export const projectTypeLabels: {
  [key: string]: { value: string; label: string };
} = {
  'utilities.gas': {
    value: 'utilities.gas',
    label: 'Utilities - Gas',
  },
  'utilities.electric': {
    value: 'utilities.electric',
    label: 'Utilities - Electric',
  },
  'utilities.water': {
    value: 'utilities.water',
    label: 'Utilities - Water',
  },
  'utilities.sewer': {
    value: 'utilities.sewer',
    label: 'Utilities - Sewer',
  },
  'utilities.storm': {
    value: 'utilities.storm',
    label: 'Utilities - Storm',
  },
  'utilities.telco': {
    value: 'utilities.telco',
    label: 'Utilities - Telecommunications',
  },
  'utilities.other': {
    value: 'utilities.other',
    label: 'Utilities - Other',
  },
  'transportation.paving': {
    value: 'transportation.paving',
    label: 'Transportation - Paving',
  },
  'transportation.bridges': {
    value: 'transportation.bridges',
    label: 'Transportation - Bridges',
  },
  'transportation.transit': {
    value: 'transportation.transit',
    label: 'Transportation - Transit',
  },
  'transportation.rail': {
    value: 'transportation.rail',
    label: 'Transportation - Rail',
  },
  'transportation.moratorium': {
    value: 'transportation.moratorium',
    label: 'Transportation - Moratorium',
  },
  'transportation.other': {
    value: 'transportation.other',
    label: 'Transportation - Other',
  },
  'realEstate.commercial': {
    value: 'realEstate.commercial',
    label: 'Real Estate - Commercial',
  },
  'realEstate.residential': {
    value: 'realEstate.residential',
    label: 'Real Estate - Residential',
  },
  'realEstate.multiFamily': {
    value: 'realEstate.multiFamily',
    label: 'Real Estate - Multi-Family',
  },
  'realEstate.office': {
    value: 'realEstate.office',
    label: 'Real Estate - Office',
  },
  'realEstate.mixedUse': {
    value: 'realEstate.mixedUse',
    label: 'Real Estate - Mixed-Use',
  },
  'realEstate.other': {
    value: 'realEstate.other',
    label: 'Real Estate - Other',
  },
};

export const billingTierLabels: Partial<{
  [tier in Project['billingTier']]: string;
}> = {
  proposal: 'Project Lite',
  project: 'Project',
  projectPlus: 'Project Plus',
};

/** Badge color and label for a project's billing tier (Draft, Project Lite, Project, Project Plus). */
export const returnProjectTypeSpecificProps = (
  billingTier: Project['billingTier'],
): { color: BadgeColor; label: string } => {
  if (billingTier === 'project') return { color: 'orange', label: 'Project' };
  if (billingTier === 'projectPlus') return { color: 'orange', label: 'Project Plus' };
  if (billingTier === 'draft') return { color: 'grey', label: 'Draft' };
  if (billingTier === 'proposal') return { color: 'blue', label: 'Project Lite' };
  return { color: 'grey', label: 'Project' };
};

export async function exportToKMZ(project: Pick<Project, 'geometry' | 'projectId' | 'name'>) {
  // NOTE - this package does not allow KMZ styling (fill, stroke, etc);
  // I think its fine for now with default white colors.
  const featureCollection: GeoJSON.FeatureCollection = {
    type: 'FeatureCollection',
    features: [createFeature(project.geometry)],
  };

  try {
    const kml = toKML(featureCollection);

    const zip = new JSZip();
    zip.file('doc.kml', kml);
    const blob = await zip.generateAsync({ type: 'blob' });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${exportFileStem(project.name, project.projectId)}_geometry.kmz`;
    a.click();
    window.URL.revokeObjectURL(url);
    captureTypedPosthogEvent(POSTHOG_EVENTS.exportToKMZ, {
      project_id: project.projectId,
    });
  } catch (err) {
    captureTypedPosthogEvent(POSTHOG_EVENTS.exportToKMZFailed, {
      project_id: project.projectId,
      reason: err instanceof Error ? err.message : 'unknown_error',
    });
    notifyExportError();
  }
}

export function copyGeometry(project: Pick<Project, 'geometry'>) {
  navigator.clipboard.writeText(
    JSON.stringify({
      type: 'FeatureCollection',
      features: [createFeature(project.geometry)],
    }),
  );
  Notify.info({ message: 'Geometry copied to clipboard' });
}

export function formatRowLabel(noun: string, name: string, id: string): string {
  return name ? `${noun} ${name} (${id})` : `${noun} ${id}`;
}

export function getProjectRowLabel(project: Pick<Project, 'name' | 'projectId'>): string {
  return formatRowLabel('project', project.name, project.projectId);
}
